export const TEAM_COUNTRY_OPTIONS = [
  { code: 'AU', label: 'AUSTRALIA (AU)' },
  { code: 'NZ', label: 'NEW ZEALAND (NZ)' },
  { code: 'CA', label: 'CANADA (CAN)' },
  { code: 'UK', label: 'UNITED KINGDOM (UK)' },
  { code: 'US', label: 'UNITED STATES (USA)' },
] as const;

export type TeamCountryCode = (typeof TEAM_COUNTRY_OPTIONS)[number]['code'];

export const DEFAULT_TEAM_COUNTRY: TeamCountryCode = 'AU';

/** Stored team country → Google Places / Geocoder ISO 3166-1 alpha-2. */
const PLACES_COUNTRY_CODE: Record<TeamCountryCode, string> = {
  AU: 'au',
  NZ: 'nz',
  CA: 'ca',
  UK: 'gb',
  US: 'us',
};

/** Team registration country → international dialing prefix. */
const TEAM_COUNTRY_DIAL_CODE: Record<TeamCountryCode, string> = {
  AU: '+61',
  NZ: '+64',
  CA: '+1',
  UK: '+44',
  US: '+1',
};

/** Team registration country → local currency. */
const TEAM_COUNTRY_CURRENCY: Record<
  TeamCountryCode,
  { code: string; symbol: string }
> = {
  AU: { code: 'AUD', symbol: 'A$' },
  NZ: { code: 'NZD', symbol: 'NZ$' },
  CA: { code: 'CAD', symbol: 'C$' },
  UK: { code: 'GBP', symbol: '£' },
  US: { code: 'USD', symbol: '$' },
};

const COUNTRY_ALIASES: Record<string, TeamCountryCode> = {
  AU: 'AU',
  AUSTRALIA: 'AU',
  NZ: 'NZ',
  'NEW ZEALAND': 'NZ',
  CA: 'CA',
  CAN: 'CA',
  CANADA: 'CA',
  UK: 'UK',
  GB: 'UK',
  'UNITED KINGDOM': 'UK',
  'GREAT BRITAIN': 'UK',
  ENGLAND: 'UK',
  US: 'US',
  USA: 'US',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
};

export function normalizeTeamCountry(value?: string | null): TeamCountryCode {
  const key = (value ?? '').trim().toUpperCase();
  return COUNTRY_ALIASES[key] ?? DEFAULT_TEAM_COUNTRY;
}

export function teamCountryToPlacesCode(code: string): string {
  return PLACES_COUNTRY_CODE[normalizeTeamCountry(code)];
}

/** Currency for a team registration country (defaults to AUD). */
export function teamCountryCurrency(country?: string | null): {
  code: string;
  symbol: string;
} {
  return TEAM_COUNTRY_CURRENCY[normalizeTeamCountry(country)];
}

/** Format a backend-fixed amount with the country's currency symbol (no conversion). */
export function formatTeamCurrencyAmount(amount: number, country?: string | null): string {
  const { symbol } = teamCountryCurrency(country);
  const rounded = Math.round(amount * 100) / 100;
  const num = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  return `${symbol}${num}`;
}

/** Dialing prefix for a team registration country (defaults to +61). */
export function teamCountryDialCode(country?: string | null): string {
  return TEAM_COUNTRY_DIAL_CODE[normalizeTeamCountry(country)];
}
